"use client";

import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";

import { API_URLS } from "./constants";
import { revalidateCourse } from "./use-course-data";
import { fetcher } from "./utils";


type TestData = {
  checkType: string,
  description: string,
  questions: {
    answer: string,
    answers: string,
    leftAnswers: string,
    multiselect: string,
    question: string,
    questionType: "open" | "closed" | "sequence" | "survey" | "matching",
    questionTypeValue: string,
    rightAnswers: string,
    shuffleButtons: string,
    status: string,
    uid: string,
    userAnswer: string,
  }[],
  session: string,
  skillLevel: string,
  status:
    "2024-05-27T13-49-53z03-00-2h2zWh" |
    "2024-06-17T14-44-44z03-00-2i0Pn2" |
    "2024-06-17T14-46-32z03-00-2i0Q0d" |
    "2024-05-20T16-54-14z03-00-2gja5B" |
    "2024-05-29T15-30-34z03-00-2h8q1D",
  statusValue: string,
  survey: string,
  remainingTime: number,
  testTimeout: string,
  testmap: string,
  title: string,
  uid: string,
};

export const useTestModel = ({
  publicationid,
  unitid,
  testid,
  enabled = true,
}: {
  publicationid: string,
  unitid: string,
  testid: string,
  enabled?: boolean,
}) => {
  return useSWR<TestData>(enabled ?
    [
      API_URLS.TEST_MODEL,
      publicationid,
      unitid,
      testid,
    ] :
    null,
  () =>
    fetcher({
      url: API_URLS.TEST_MODEL,
      params: {
        publicationid,
        unitid,
        testid,
        hidejson: false,
      },
    }));
};

type TestQuestionData = {
  answer: string,
  answers: string,
  currentAnswers: string,
  fileAnswer: string,
  leftAnswers: string,
  rightAnswers: string,
  multiselect: string,
  question: string,
  questionType: "open" | "closed" | "sequence" | "survey" | "matching",
  questionTypeValue: string,
  shuffleButtons: string,
  status: string,
  uid: string,
  userAnswer: string,
  ball?: string | null,
  maxball?: string | null,
  uploadFile?: string,
  questionImage?: string,
  validation?: string,
  validationInfo?: string,
  surveyComment1: string,
  surveyComment2: string,
  stars: string,
  starsCount: string,
  requiredFeedback: string,
  additionalQuestionTitle: string,
};

export const useTestQuestionData = ({
  publicationId,
  unitId,
  questionId,
  session,
  testId,
  profile: _profile,
  showResults: _showResults,
}: {
  publicationId: string,
  unitId: string,
  questionId?: string,
  session?: string,
  testId?: string,
  profile?: string,
  showResults?: boolean,
}) => {
  const isStopRequest = !questionId || !session || !testId;
  return useSWR<TestQuestionData>(isStopRequest ?
    null :
    [
      API_URLS.TEST_QUESTION_MODEL,
      publicationId,
      unitId,
      questionId,
      session,
    ],
  () =>
    fetcher({
      url: API_URLS.TEST_QUESTION_MODEL,
      params: {
        publicationid: publicationId,
        unitid: unitId,
        // testid: testId!,
        // FIXME maybe make nice in future
        questionid: questionId!,
        // FIXME maybe make nice in future
        session: session!,
        hidejson: false,
      },
    }));
};

export const useTestQuestionUpdate = ({
  publicationId,
  unitId,
  questionId,
  session,
  questionType,
}: {
  publicationId: string,
  unitId: string,
  questionId?: string,
  session?: string,
  questionType?: "open" | "closed" | "sequence" | "survey" | "matching",
}) => {
  const isStopRequest = !questionId || !session;

  return useSWRMutation(isStopRequest ?
    null :
    API_URLS.TEST_QUESTION_UPDATE,
  async (_url,
    {
      arg: {
        answerValue,
        fileAnswer,
        signal,
        userComment,
      },
    }: { arg: {
      answerValue: string,
      fileAnswer?: string,
      signal?: AbortSignal,
      userComment?: string,
    }, }) => {
    const res = await fetcher({
      url: API_URLS.TEST_QUESTION_UPDATE,
      method: "GET",
      params: {
        publicationid: publicationId,
        unitid: unitId,
        questionid: questionId!,
        // eslint-disable-next-line camelcase
        question_type: questionType!,
        session: session!,
        answervalue: answerValue,
        // eslint-disable-next-line camelcase
        file_answer: fileAnswer ?? "",
        // eslint-disable-next-line camelcase
        user_comment: userComment ?? "",
      },
      signal,
    });

    if (res) {
      mutate(
        [
          API_URLS.TEST_QUESTION_MODEL,
          publicationId,
          unitId,
          questionId,
          session,
        ],
        undefined,
        false,
      );
    }
  });
};

type FinishTestResult = {
  expired?: boolean,
  analyticsset?: string,
};

export const useTestFinish = ({
  publicationId,
  unitId,
  testId,
  questionId,
  session,
  mainUnitId,
  timeout,
  skillLevel,
  survey = false,
}: {
  publicationId: string,
  unitId: string,
  testId?: string,
  questionId?: string,
  session?: string,
  mainUnitId?: string,
  timeout?: string,
  skillLevel?: string,
  survey?: boolean,
}) => {
  return useSWRMutation(!testId || !session ?
    null :
    API_URLS.TEST_FINISH,
  async (url) => {
    const res = await fetcher<FinishTestResult>({
      url,
      method: "GET",
      params: {
        publicationid: publicationId,
        unitid: unitId,
        testid: testId!,
        session: session!,
        // eslint-disable-next-line camelcase
        skill_level: skillLevel ?? "",
        timeout: timeout ?? "",
        survey,
      },
    });

    if (res) {
      mutate(
        [
          API_URLS.TEST_QUESTION_MODEL,
          publicationId,
          unitId,
          questionId,
          session,
        ],
        undefined,
        false,
      );
      revalidateCourse(publicationId, mainUnitId);
      return res;
    }
  });
};

export const useAbandonAttempt = () => {
  return useSWRMutation(API_URLS.TEST_SESSION_RESET,
    async (_url, { arg: { session }}: { arg: { session: string }}) => {
      try {
        await fetcher({
          url: API_URLS.TEST_SESSION_RESET,
          method: "GET",
          params: {
            session,
          },
        });
      } catch (error) {
        console.error(error);
      }
    });
};

type LastSessionForTestData = {
  sessionUid: string,
};

export const useLastSessionForTest = ({
  testId,
  enabled = true,
}: {
  testId?: string,
  enabled?: boolean,
}) => {
  return useSWR<LastSessionForTestData>(enabled && testId ?
    [ API_URLS.TEST_LAST_SESSION, testId ] :
    null,
  () =>
    fetcher({
      url: API_URLS.TEST_LAST_SESSION,
      params: {
        test: testId!,
      },
    }));
};
